import { loadStripe } from '@stripe/stripe-js';
import { supabase } from './supabase';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

// Subscription tiers and payment links
export const SUBSCRIPTION_TIERS = {
  FREE: 'free',
  BASIC: 'prod_basic123', // replace later
  PRO: 'prod_Rnha7reQEpzEdg',
  UNLIMITED: 'prod_unlimited123' // replace later
} as const;

export const SUBSCRIPTION_FEATURES = {
  PRO: {
    name: 'Pro',
    price: 9.99,
    description: 'Full access to all features',
    features: [
      'Unlimited professor searches',
      'Advanced email generation',
      'Track unlimited professors',
      'Priority support',
      'Advanced analytics'
    ]
  }
};
// Update success URL to include return_to parameter
export const PAYMENT_LINKS = {
  BASIC: `https://buy.stripe.com/test_basic_link?return_url=${encodeURIComponent(window.location.origin + '/dashboard')}`,
  PRO: `https://buy.stripe.com/test_00g8ydcLW3oSa0U9AA?return_url=${encodeURIComponent(window.location.origin + '/dashboard')}`,
  UNLIMITED: `https://buy.stripe.com/test_unlimited_link?return_url=${encodeURIComponent(window.location.origin + '/dashboard')}`
};

// Redirect to Stripe payment page
export const redirectToPayment = () => {
  window.location.href = PAYMENT_LINKS.PRO;
};

// Updated function to check the subscription status
export const getSubscriptionStatus = async (): Promise<{ tier: string; subscription?: any }> => {
  try {
    // Get current user session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session || !session.user) {
      return { tier: SUBSCRIPTION_TIERS.FREE };
    }

    // First check Supabase subscriptions table
    const { data: subscription, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', session.user.id)
      .eq('status', 'active')
      .maybeSingle();

    if (!error && subscription?.price_id === SUBSCRIPTION_TIERS.PRO) {
      return {
        tier: SUBSCRIPTION_TIERS.PRO,
        subscription
      };
    }

    // If no subscription found in Supabase, check Stripe using email
    const stripe = await loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);
    if (!stripe) {
      return { tier: SUBSCRIPTION_TIERS.FREE };
    }

    const response = await fetch('/api/check-stripe-subscription', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: session.user.email
      })
    });

    if (response.ok) {
      const { hasActiveSubscription } = await response.json();
      
      if (hasActiveSubscription) {
        // Create subscription record in Supabase
        const { data: newSubscription } = await supabase
          .from('subscriptions')
          .upsert({
            user_id: session.user.id,
            status: 'active',
            price_id: SUBSCRIPTION_TIERS.PRO,
            stripe_customer_email: session.user.email
          })
          .select()
          .single();

        return {
          tier: SUBSCRIPTION_TIERS.PRO,
          subscription: newSubscription
        };
      }
    }

    return { tier: SUBSCRIPTION_TIERS.FREE };
  } catch (error) {
    console.error('Error checking subscription:', error);
    return { tier: SUBSCRIPTION_TIERS.FREE };
  }
};

// Check usage limits
export const checkUsageLimit = async (): Promise<{
  allowed: boolean;
  remaining: number;
  message?: string;
}> => {
  // For now, allow all operations
  return {
    allowed: true,
    remaining: 100
  };
};

// Add a webhook handler endpoint in your backend to handle Stripe events
export const handleStripeWebhook = async (event: any) => {
  const { type, data: { object } } = event;

  switch (type) {
    case 'checkout.session.completed':
      // Add subscription record when payment is successful
      const { customer_email, client_reference_id, subscription } = object;
      
      const { data: user } = await supabase
        .from('users')
        .select('id')
        .eq('email', customer_email)
        .single();

      if (user) {
        await supabase
          .from('subscriptions')
          .upsert({
            user_id: user.id,
            status: 'active',
            price_id: SUBSCRIPTION_TIERS.PRO,
            stripe_subscription_id: subscription
          });
      }
      break;

    case 'customer.subscription.deleted':
      // Update subscription status when canceled
      const { id: subscription_id } = object;
      await supabase
        .from('subscriptions')
        .update({ status: 'canceled' })
        .eq('stripe_subscription_id', subscription_id);
      break;
  }
};

export default stripePromise;